import { observer } from 'mobx-react';
import React, { useEffect, useRef } from 'react';
import { Row } from 'react-table';
import { fileCabinetRecordId } from '../../services/file-cabinet/file-cabinet.types';
import type { FileCabinetRecord, FileCabinetViewKey } from '../../services/file-cabinet/file-cabinet.types';
import { FileCabinetStore } from '../../stores/file-cabinet/file-cabinet.store';
import { DataTable, ITableFetchOptions } from '../data-table/data-table';
import { DataTablePresenter } from '../data-table/data-table.presenter';
import { FileCabinetFtsStrip } from './file-cabinet-fts-strip';
import { FileCabinetSearch } from './file-cabinet-search';
import * as S from './file-cabinet.styles';

interface IFileCabinetTableProps {
    store: FileCabinetStore;
    /** The view this table was mounted for. Its fetches say so, and the store drops any for another. */
    view: FileCabinetViewKey;
    presenter: DataTablePresenter<FileCabinetRecord>;
}

/** The table's default page size is 20, so it is one of the options. */
const PAGE_SIZES = [20, 50, 100];

/**
 * The File Cabinet table: the core `DataTable`, so paging, sorting, the Filters panel and the column
 * picker are the same controls as on every other screen (NS-1). The search box and record count sit
 * at the start of its controls row; a row click opens the drawer.
 */
export const FileCabinetTable = observer(({ store, view, presenter }: IFileCabinetTableProps): React.ReactElement => {
    // The table debounces its fetches and never cancels a pending one when it unmounts. A view check
    // alone is not enough: All Documents → Mail → All Documents inside the debounce brings the FIRST
    // docs table's request — with a search no longer in the box — to a store showing docs again. So
    // a fetch from an unmounted table is dropped here, at its source.
    const mounted = useRef(true);
    useEffect(() => {
        mounted.current = true;
        return (): void => {
            mounted.current = false;
        };
    }, []);

    const openId = store.drawer?.view === store.view ? fileCabinetRecordId(store.view, store.drawer.record) : undefined;

    return (
        <DataTable
            presenter={presenter}
            isLoading={store.loading}
            pageSizeOptions={PAGE_SIZES}
            onFetch={(options: ITableFetchOptions): void => {
                if (mounted.current) {
                    void store.loadPage(options, view);
                }
            }}
            onRowClick={(row: Row<FileCabinetRecord>): void => store.openRecord(row.original)}
            rowClass={(row: Row<FileCabinetRecord>): string =>
                openId && fileCabinetRecordId(view, row.original) === openId ? 'file-cabinet-open-row' : ''
            }
            renderControlsLeading={(): React.ReactElement => (
                <S.Leading>
                    <FileCabinetSearch store={store} />
                    <S.RecordCount aria-live="polite">{recordLabel(store)}</S.RecordCount>
                </S.Leading>
            )}
            renderToolbar={(): React.ReactElement => <FileCabinetFtsStrip store={store} />}
            NotFoundComponent={(): React.ReactElement => <FileCabinetEmpty store={store} />}
        />
    );
});

/**
 * What an empty table says. The core table shows this whenever it has no rows and is not loading —
 * after a failed request too — so "no records" over a list that never loaded must not happen (NS-3).
 */
const FileCabinetEmpty = observer(({ store }: { store: FileCabinetStore }): React.ReactElement => {
    if (store.error?.forbidden) {
        return (
            <S.Empty>
                <S.EmptyTitle>No access.</S.EmptyTitle>
                <S.EmptyHint>This account cannot read this list. Whether it holds records is not known.</S.EmptyHint>
            </S.Empty>
        );
    }
    if (store.error) {
        return (
            <S.Empty>
                <S.EmptyTitle>Couldn't load this list.</S.EmptyTitle>
                <S.EmptyHint>There may be records we couldn't show. Retry from the banner above.</S.EmptyHint>
            </S.Empty>
        );
    }
    if (store.lastRequestFiltered) {
        return (
            <S.Empty>
                <S.EmptyTitle>No results.</S.EmptyTitle>
                <S.EmptyHint>Adjust the search or filters.</S.EmptyHint>
            </S.Empty>
        );
    }
    return (
        <S.Empty>
            <S.EmptyTitle>Nothing here yet.</S.EmptyTitle>
            {store.dataSource.emptyNote?.(store.view) && <S.EmptyHint>{store.dataSource.emptyNote(store.view)}</S.EmptyHint>}
        </S.Empty>
    );
});

/** "Loading…" and "unavailable" are not counts; only a loaded page gets a number (NS-3). */
function recordLabel(store: FileCabinetStore): string {
    if (store.error?.forbidden) {
        return 'No access';
    }
    if (store.error) {
        return 'Records unavailable';
    }
    if (store.total === undefined) {
        return 'Loading…';
    }
    return `${store.total.toLocaleString()} ${store.total === 1 ? 'record' : 'records'}`;
}
